import React from 'react';
import { ArrowLeft, Share2 } from 'lucide-react';

/**
 * Layout base para todas as telas de storytelling — v5
 * 
 * Tipografia: Raleway em toda a interface (corpo + UI)
 * Narrative text: Raleway Light 300, #b8bade (silver), line-height 1.75
 * CTA: Raleway SemiBold 600, accent bg
 * Título funcional: Raleway Medium 500 (NÃO é entry point, é dado)
 */

const FONT_BODY = "'Raleway', -apple-system, sans-serif";
const ACCENT = '#50cfad';
const DEEP = '#060550';

export default function StorytellingLayout({
  backgroundImage,
  title,
  children,
  narrativeText,
  stepIndicator,
  ctaText,
  onContinue,
  onBack,
  onShare,
  showBackButton = true
}) {
  const defaultImage = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80';

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: FONT_BODY,
      backgroundColor: '#000'
    }}>
      {/* Background Image com Blur */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `url(${backgroundImage || defaultImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(40px)',
        transform: 'scale(1.1)',
        zIndex: 0
      }} />

      {/* Overlay azul degradê */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(9, 7, 119, 0.75) 0%, rgba(9, 7, 119, 0.85) 100%)',
        zIndex: 1
      }} />

      {/* Content scrollável */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        paddingTop: '3rem',
        paddingBottom: ctaText ? '140px' : '2rem',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 1rem',
          marginBottom: '0.5rem'
        }}>
          {showBackButton ? (
            <button 
              onClick={onBack}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              <ArrowLeft size={24} strokeWidth={2} />
            </button>
          ) : <div style={{ width: 40 }} />}
          
          <button 
            onClick={onShare}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              color: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            <Share2 size={24} strokeWidth={2} />
          </button>
        </div>

        {/* Título funcional (Raleway Medium, NÃO serif — é dado, não entry point) */}
        {title && (
          <div style={{ textAlign: 'center', padding: '0 1rem', marginBottom: '1.5rem' }}>
            <h2 style={{
              fontFamily: FONT_BODY,
              fontSize: '1.125rem',
              fontWeight: 500,
              color: '#f0f0f8',
              lineHeight: 1.3,
              letterSpacing: '0.02em',
              margin: 0
            }}>
              {title}
            </h2>
          </div>
        )}

        {/* Conteúdo principal (cards, carrossel, etc) */}
        <div style={{ flex: 1, padding: '0 1rem' }}>
          {children}
        </div>

        {/* Texto narrativo — Raleway Light 300, silver, line-height 1.75 */}
        {narrativeText && (
          <div style={{ padding: '0 1.5rem', marginTop: '1rem' }}>
            <p style={{
              fontFamily: FONT_BODY,
              fontWeight: 300,
              fontSize: 'clamp(0.92rem, 2.7vw, 1rem)',
              lineHeight: 1.75,
              color: '#b8bade',
              margin: 0
            }}>
              {narrativeText}
            </p>
          </div>
        )}
      </div>

      {/* Footer fixo com CTA */}
      {ctaText && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          padding: '1rem',
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))',
          background: 'linear-gradient(180deg, transparent 0%, rgba(9, 7, 119, 0.95) 30%)'
        }}>
          {/* Indicador de passo */}
          {stepIndicator && (
            <p style={{
              fontFamily: FONT_BODY,
              fontSize: '0.7rem',
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(255, 255, 255, 0.22)',
              textAlign: 'center',
              margin: '0 0 0.75rem 0',
            }}>
              {stepIndicator}
            </p>
          )}

          {/* CTA Button — Raleway SemiBold 600, accent bg */}
          <button
            onClick={onContinue}
            style={{
              display: 'block',
              width: '100%',
              padding: '16px 24px',
              borderRadius: '50px',
              background: ACCENT,
              color: DEEP,
              fontFamily: FONT_BODY,
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textAlign: 'center',
              border: 'none',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
          >
            {ctaText}
          </button>
        </div>
      )}
    </div>
  );
}
